/* eslint-disable no-confusing-arrow */
import { findIndex } from 'lodash';
import {
  useSearchParams,
  useLocation,
  createSearchParams,
  useNavigate,
  useParams,
} from 'react-router';
import qs from 'qs';
import { useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { AssetContext, ParametersContext } from 'context';

import { parametersApi, handleApiError } from 'api';

const useTreeItem = (node, isHierarchy, setExpanded) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { search } = useLocation();
  const { projectId } = useParams();
  const searchString = {
    ...qs.parse(search, { ignoreQueryPrefix: true }),
  };
  const { theAsset, getAssets, allAssets, setTheAsset, setAllAssets } =
    useContext(AssetContext);

  let setAsset = () => {};
  if (theAsset?.id === node.id) {
    setAsset = setTheAsset;
  }
  delete searchString.project_parameter;
  const [displayWarning, setDisplayWarning] = useState({});
  const [openSnackBar, setOpenSnackBar] = useState({
    open: false,
    variant: '',
  });
  const { getParameters, setParameters } = useContext(ParametersContext);
  const [isDuplicateAssetOpen, setIsDuplicateAssetOpen] = useState(false);
  const [isAddNewAssetOpen, setIsAddNewAssetOpen] = useState(false);
  const [openEditingForm, setOpenEditingForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreVertVisible, setMoreVertVisible] = useState(false);
  const getSetMoreVertVisible = () =>
    menuOpen ? () => {} : setMoreVertVisible;
  const { enqueueSnackbar } = useSnackbar();
  const warningClose = () => {
    setDisplayWarning({
      open: false,
    });
  };

  const deleteAsset = async () => {
    setParameters([]);
    try {
      await parametersApi('deleteAsset', {
        asset_id: node.id,
      });

      setAsset({ ...node, deletedAt: 'deleted' });
      setAllAssets(curr => {
        const indexOfUpdate = findIndex(
          curr,
          existing => existing.id === node.id
        );
        curr[indexOfUpdate] = { ...curr[indexOfUpdate], deletedAt: 'deleted' };
      });
      searchParams.set('show_deleted_assets', true);
      searchParams.set('show_deleted_parameters', true);
      setSearchParams(searchParams);
      setOpenSnackBar({
        open: true,
        variant: 'success',
        message: 'Asset successfully deleted',
      });
    } catch (error) {
      setOpenSnackBar({
        open: true,
        variant: 'error',
        message: error?.response?.data?.details || error.message,
      });
      handleApiError(error, []);
    } finally {
      getParameters();
    }
  };

  const restoreAsset = async () => {
    setParameters([]);
    try {
      const response = await parametersApi('patchAsset', {
        asset_id: node.id,
        body: {
          name: null,
          restore: true,
        },
      });
      if (response) {
        setAsset(response.asset);
        setSearchParams(searchParams);
        setAllAssets(curr => {
          const indexOfUpdate = findIndex(
            curr,
            existing => existing.id === node.id
          );
          curr[indexOfUpdate] = { ...curr[indexOfUpdate], deletedAt: null };
        });
      }
      setOpenSnackBar({
        open: true,
        variant: 'success',
        message: 'Asset successfully restored',
      });
    } catch (error) {
      setOpenSnackBar({
        open: true,
        variant: 'error',
        message: error?.response?.data?.details || error.message,
      });
      handleApiError(error, []);
    } finally {
      getParameters();
    }
  };

  const handleDeleteClick = () => {
    setDisplayWarning({
      open: true,
      handleAccept: () => {
        deleteAsset();
        warningClose();
      },
      handleClose: warningClose,
      title: 'Are you sure you want to remove this Asset?',
      body: 'You can restore it later',
      secondaryButtonText: 'Cancel',
      primaryButtonText: 'Remove',
    });
  };

  const handleRestoreClick = () => {
    setDisplayWarning({
      open: true,
      handleAccept: () => {
        restoreAsset();
        warningClose();
      },
      handleClose: warningClose,
      title: 'Are you sure you want to restore this Asset?',
      body: 'All parameters of this asset will be restored also, do you want to restore this asset and its parameters?',
      secondaryButtonText: 'Cancel',
      primaryButtonText: 'Restore',
    });
  };

  useEffect(() => {
    if (openSnackBar.open) {
      const title =
        openSnackBar.variant === 'success'
          ? openSnackBar.message
          : `Error: ${openSnackBar.message}`;
      enqueueSnackbar(title, {
        variant: openSnackBar.variant,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSnackBar]);

  const onOpenClick = () => {
    if (!isHierarchy) return;
    if (!node.id.includes('_')) {
      if (node.id !== theAsset.id) {
        const selected = allAssets.find(asset => asset.id === node.id);
        const chunkSize = 99;

        Array.from(
          { length: Math.ceil(selected.children.length / chunkSize) },
          (_, i) =>
            getAssets({
              asset_id: selected.children.slice(
                i * chunkSize,
                i * chunkSize + chunkSize
              ),
            })
        );
      }
    }
    setExpanded(curr => [...curr, node.id]);
  };

  const onNodeToggle = () => {
    if (!isHierarchy) return;
    navigate({
      pathname: `/projects/${projectId}/asset/${node.id}/parameters`,
      search: `?${createSearchParams({
        ...searchString,
      })}`,
    });
  };

  return {
    onNodeToggle,
    onOpenClick,
    handleDeleteClick,
    handleRestoreClick,
    isDuplicateAssetOpen,
    setIsDuplicateAssetOpen,
    isAddNewAssetOpen,
    setIsAddNewAssetOpen,
    openEditingForm,
    setOpenEditingForm,
    menuOpen,
    setMenuOpen,
    moreVertVisible,
    setMoreVertVisible,
    getSetMoreVertVisible,
    displayWarning,
    deleteAsset,
    restoreAsset,
    openSnackBar,
  };
};

export default useTreeItem;
